import { useEffect, useRef, type ReactNode } from 'react'
import { useLocation } from 'react-router'

import { FullPageLoader } from '@/components/states'
import { AuthConfigurationNotice, SignInPrompt } from '@/auth/AuthNotices'
import { useAuth } from '@/auth/useAuth'

/**
 * Route guard for `/app/**`. An unauthenticated visitor is sent straight to
 * Keycloak, remembering where they were headed.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, configured, error, login } = useAuth()
  const location = useLocation()
  const redirectStarted = useRef(false)

  const returnTo = `${location.pathname}${location.search}`

  useEffect(() => {
    if (status === 'unauthenticated' && configured && !redirectStarted.current) {
      redirectStarted.current = true
      login(returnTo)
    }
  }, [configured, login, returnTo, status])

  if (!configured) {
    return <AuthConfigurationNotice />
  }

  if (status === 'error') {
    return (
      <SignInPrompt
        message={error ?? 'Sign-in failed.'}
        onRetry={() => {
          login(returnTo)
        }}
      />
    )
  }

  if (status === 'authenticated') {
    return <>{children}</>
  }

  return <FullPageLoader label="Checking your session…" />
}
